import { GROUP_TYPE_KEYS } from '@pushly/pquery-core'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'

/** The group keys the wire format defines, from the engine rather than a copy kept here. */
const GROUP_KEYS = [...GROUP_TYPE_KEYS]

/**
 * Every property key a stored document references, in document order.
 *
 * Group keys are followed rather than collected, so a negated group contributes
 * the properties inside it and not its own name. Condition values are not
 * descended into: the key naming a subquery is the property, and the keys inside
 * it belong to a different document.
 */
export function collectProperties(doc: unknown, found: string[] = []): string[] {
    if (!doc || typeof doc !== 'object') return found

    const record = doc as Record<string, unknown>
    const group = GROUP_KEYS.map((key) => record[key]).find(Array.isArray)

    if (group) {
        ;(group as unknown[]).forEach((entry) => collectProperties(entry, found))
        return found
    }

    Object.keys(record).forEach((key) => {
        if (key !== 'meta' && !found.includes(key)) found.push(key)
    })

    return found
}

/**
 * The document's property keys the given catalog does not describe.
 *
 * The set-difference of what the document references against what the catalog
 * carries, in document order. An empty result means every condition still has a
 * home in the catalog — the caller uses that to decide whether narrowing the
 * catalog would strand any condition.
 */
export function collectUnsupportedProperties(doc: unknown, fields: PQueryBuilderField[]): string[] {
    const known = new Set(fields.map((field) => field.key))
    return collectProperties(doc).filter((property) => !known.has(property))
}

/**
 * Removes every condition the given catalog cannot describe, and any group left
 * empty by those removals.
 *
 * A condition is identified by its single property key; when that key is not one
 * the catalog carries, the builder can neither render the condition nor preserve
 * it on save, so it is dropped rather than silently lost. Groups follow the same
 * shape collectProperties walks — an and/or/and_not whose entries all drop away is
 * itself dropped, except the root, which is kept as an empty group so the result
 * is still a valid query the builder can open.
 */
export function pruneUnsupportedConditions(doc: unknown, fields: PQueryBuilderField[]): Record<string, unknown> {
    const known = new Set(fields.map((field) => field.key))
    const rootGroupKey = GROUP_KEYS.find((key) => Array.isArray((doc as Record<string, unknown> | null)?.[key])) ?? 'and'

    return pruneNode(doc, known) ?? { [rootGroupKey]: [] }
}

function pruneNode(node: unknown, known: Set<string>): Record<string, unknown> | null {
    if (!node || typeof node !== 'object') return null

    const record = node as Record<string, unknown>
    const groupKey = GROUP_KEYS.find((key) => Array.isArray(record[key]))

    if (groupKey) {
        const kept = (record[groupKey] as unknown[])
            .map((entry) => pruneNode(entry, known))
            .filter((entry): entry is Record<string, unknown> => entry !== null)

        // A group emptied by pruning carries nothing, so it is dropped in turn; the
        // root is restored to an empty group by the caller above.
        if (kept.length === 0) return null

        const rest = Object.fromEntries(Object.entries(record).filter(([key]) => key !== groupKey))
        return { ...rest, [groupKey]: kept }
    }

    // A condition names exactly one property; when the catalog no longer carries
    // it, the whole condition goes.
    const propertyKeys = Object.keys(record).filter((key) => key !== 'meta')
    if (propertyKeys.some((key) => !known.has(key))) return null

    return record
}
